import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CurrencyRupeeIcon,
  UserIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, format } from 'date-fns';
import { collaborationAPI } from '../services/api';
import { formatCurrency } from '../utils/format';
import Button from '../components/common/Button';
import toast from 'react-hot-toast';
import RequestDetailModal from '../components/collaboration/RequestDetailModal';
import Avatar from '../components/common/Avatar';
import ConfirmationModal from '../components/common/ConfirmationModal';
import CancellationReasonModal from '../components/collaboration/CancellationReasonModal';
import CollaborationCompletionModal from '../components/collaboration/CollaborationCompletionModal';
import CreateCollaborationModal from '../components/collaboration/CreateCollaborationModal';
import { useAppSelector } from '../hooks/redux';

import { useLocation, useNavigate } from 'react-router-dom';

const CollaborationRequestsPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [requests, setRequests] = useState<any[]>([]);
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalSent, setTotalSent] = useState(0);
  
  const [activeTab, setActiveTab] = useState<'received' | 'sent'>(
    user?.user_type === 'hirer' ? 'sent' : 'received'
  );

  useEffect(() => {
    if (user?.user_type === 'hirer') {
      setActiveTab('sent');
    }
  }, [user?.user_type]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Infinite Scroll Refs
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const fetchRequests = useCallback(async (pageNum: number, isLoadMore: boolean = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await collaborationAPI.getCollaborationRequests({
        type: activeTab,
        status: statusFilter === 'all' ? undefined : statusFilter,
        is_paid: paymentFilter === 'all' ? undefined : paymentFilter === 'paid',
        collab_type: typeFilter === 'all' ? undefined : typeFilter,
        page: pageNum,
        limit: 20
      });

      const { data, pagination } = response.data.data;
      
      // The backend returns { sentRequests: [], receivedRequests: [] } inside data.data
      // Handle deep linking from notifications
  // TODO: Re-implement deep linking with pagination support (fetching specific request by ID)
  /*
  useEffect(() => {
    const state = location.state as { selectedRequestId?: string };
    const searchParams = new URLSearchParams(location.search);
    const queryRequestId = searchParams.get('requestId');
    
    const requestId = state?.selectedRequestId || queryRequestId;

    if (requestId) {
       // Logic to fetch/find request would go here
    }
  }, [location.state, location.search, navigate, location.pathname]);
  */
      // But since we are filtering by type, we should look at the relevant array
      // Wait, let's check the backend response structure again from the file view
      // It returns "data": { "sentRequests": ..., "receivedRequests": ... }
      
      const newRequests = (activeTab === 'sent' ? data.sentRequests : data.receivedRequests) || [];
      
      if (isLoadMore) {
        setRequests(prev => [...prev, ...newRequests]);
      } else {
        setRequests(newRequests);
      }
      
      setHasMore(pagination.current_page < pagination.total_pages);
      
      // Update counts if available (using total from pagination as a proxy for the current tab's total)
      if (activeTab === 'received') {
        setTotalReceived(pagination.total);
      } else {
        setTotalSent(pagination.total);
      }

    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, statusFilter, paymentFilter, typeFilter]);

  // Initial Fetch and Filter Changes
  useEffect(() => {
    setPage(1);
    fetchRequests(1, false);
    
    // Mark notifications as read
    collaborationAPI.markAllNotificationsAsRead().catch(err => {
      console.error('Failed to mark notifications as read:', err);
    });
  }, [fetchRequests]);

  // Fetch counts for both tabs on mount
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [receivedRes, sentRes] = await Promise.all([
          collaborationAPI.getCollaborationRequests({ type: 'received', limit: 1 }),
          collaborationAPI.getCollaborationRequests({ type: 'sent', limit: 1 })
        ]);
        setTotalReceived(receivedRes.data.data.pagination.total);
        setTotalSent(sentRes.data.data.pagination.total);
      } catch (error) {
        console.error('Failed to fetch counts:', error);
      }
    };
    fetchCounts();
  }, []);

  // Infinite Scroll Observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchRequests(nextPage, true);
        }
      },
      { rootMargin: '800px' }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loading, loadingMore, page, fetchRequests]);

  const handleResponse = async (requestId: string, status: 'accepted' | 'declined') => {
    try {
      await collaborationAPI.respondToCollaborationRequest(requestId, {
        status,
        response_message: status === 'accepted' 
          ? "I'd love to collaborate with you! Let's discuss the details."
          : "Thank you for your interest, but I'm not available for this collaboration."
      });
      
      toast.success(`Request ${status} successfully!`);
      // Refresh current page
      fetchRequests(page, false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Failed to respond to request:', error);
      toast.error('Failed to respond to request');
    }
  };

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [requestToWithdraw, setRequestToWithdraw] = useState<string | null>(null);

  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showCreateCollabModal, setShowCreateCollabModal] = useState(false);
  const [requestToProcess, setRequestToProcess] = useState<any>(null);

  const handleWithdraw = (requestId: string) => {
    setRequestToWithdraw(requestId);
    setShowWithdrawModal(true);
  };

  const confirmWithdraw = async () => {
    if (!requestToWithdraw) return;

    try {
      await collaborationAPI.withdrawCollaborationRequest(requestToWithdraw);
      toast.success('Request withdrawn successfully');
      fetchRequests(page, false);
      setSelectedRequest(null);
      setShowWithdrawModal(false);
      setRequestToWithdraw(null);
    } catch (error) {
      console.error('Failed to withdraw request:', error);
      toast.error('Failed to withdraw request');
    }
  };

  const handleComplete = async (request: any, alreadyConfirmed: boolean = false) => {
    setRequestToProcess(request);
    
    const isSender = user?.id === request.sender_id;
    const isReceiver = user?.id === request.receiver_id;
    const hasOtherMarkedComplete = isSender ? request.receiver_marked_complete : isReceiver ? request.sender_marked_complete : false;
    
    if (hasOtherMarkedComplete && !alreadyConfirmed) {
      setShowCompleteConfirm(true);
    } else if (hasOtherMarkedComplete && alreadyConfirmed) {
      setShowCompletionModal(true);
    } else {
      // First user: immediately complete request in background without modals
      try {
        const res = await collaborationAPI.completeCollaborationRequest(request.id);
        const updatedReq = res.data.data?.data || res.data.data || request;
        toast.success('You marked collaboration as complete. Awaiting partner confirmation.');
        
        if (selectedRequest && selectedRequest.id === request.id) {
          setSelectedRequest({
            ...selectedRequest,
            status: updatedReq.status,
            sender_marked_complete: isSender ? true : selectedRequest.sender_marked_complete,
            receiver_marked_complete: !isSender ? true : selectedRequest.receiver_marked_complete,
            updated_at: updatedReq.updated_at || new Date().toISOString(),
          });
        }
        fetchRequests(page, false);
      } catch (error) {
        toast.error('Failed to complete collaboration');
      }
    }
  };

  const confirmComplete = () => {
    setShowCompleteConfirm(false);
    if (requestToProcess) {
      handleComplete(requestToProcess, true);
    }
  };

  const handleCancel = (request: any) => {
    setRequestToProcess(request);
    setShowCancellationModal(true);
  };

  const handleRevokeComplete = async (request: any) => {
    try {
      await collaborationAPI.revokeCompleteCollaborationRequest(request.id);
      toast.success('Completion mark revoked!');
      fetchRequests(page, false);
      if (selectedRequest && selectedRequest.id === request.id) {
        setSelectedRequest({
          ...selectedRequest,
          sender_marked_complete: user?.id === selectedRequest.sender_id ? false : selectedRequest.sender_marked_complete,
          receiver_marked_complete: user?.id === selectedRequest.receiver_id ? false : selectedRequest.receiver_marked_complete,
        });
      }
    } catch (error) {
      toast.error('Failed to revoke completion mark');
    }
  };

  const onConfirmCompletionPostNow = () => {
    setShowCompletionModal(false);
    setShowCreateCollabModal(true);
  };

  const onConfirmCompletionPostLater = async () => {
    if (!requestToProcess) return;
    try {
      const res = await collaborationAPI.completeCollaborationRequest(requestToProcess.id);
      const updatedReq = res.data.data?.data || res.data.data || requestToProcess;
      toast.success('Collaboration marked as complete!');
      setShowCompletionModal(false);

      if (selectedRequest && selectedRequest.id === requestToProcess.id) {
        const isSender = user?.id === selectedRequest.sender_id;
        setSelectedRequest({
          ...selectedRequest,
          status: updatedReq.status || (isSender && selectedRequest.receiver_marked_complete ? 'completed' : selectedRequest.status),
          sender_marked_complete: isSender ? true : selectedRequest.sender_marked_complete,
          receiver_marked_complete: !isSender ? true : selectedRequest.receiver_marked_complete,
          updated_at: updatedReq.updated_at || new Date().toISOString(),
        });
      }

      setRequestToProcess(null);
      fetchRequests(page, false);
    } catch (error) {
      toast.error('Failed to complete collaboration');
    }
  };

  const onConfirmCancellation = async (reason: string, details: string) => {
    if (!requestToProcess) return;
    try {
      await collaborationAPI.cancelCollaborationRequest(requestToProcess.id, { reason, details });
      toast.success('Collaboration cancelled');
      setShowCancellationModal(false);

      if (selectedRequest && selectedRequest.id === requestToProcess.id) {
        setSelectedRequest({
          ...selectedRequest,
          status: 'cancelled',
          cancellation_reason: reason,
          cancellation_details: details,
        });
      }

      setRequestToProcess(null);
      fetchRequests(page, false);
    } catch (error) {
      toast.error('Failed to cancel collaboration');
    }
  };

  const handlePostSuccess = async () => {
    if (!requestToProcess) return;
    try {
       // Also mark the request as complete in backend if not already done
       const res = await collaborationAPI.completeCollaborationRequest(requestToProcess.id);
       const updatedReq = res.data.data?.data || res.data.data || requestToProcess;
       
       if (selectedRequest && selectedRequest.id === requestToProcess.id) {
         const isSender = user?.id === selectedRequest.sender_id;
         setSelectedRequest({
           ...selectedRequest,
           status: updatedReq.status || (isSender && selectedRequest.receiver_marked_complete ? 'completed' : selectedRequest.status),
           sender_marked_complete: isSender ? true : selectedRequest.sender_marked_complete,
           receiver_marked_complete: !isSender ? true : selectedRequest.receiver_marked_complete,
           updated_at: updatedReq.updated_at || new Date().toISOString(),
         });
       }

       setShowCreateCollabModal(false);
       setRequestToProcess(null);
       fetchRequests(page, false);
    } catch (error) {
       console.error("Failed to mark as complete after post", error);
    }
  };

  const renderRequestCard = (request: any) => (
    <motion.div
      key={request.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => setSelectedRequest(request)}
      className={`rounded-xl p-5 hover:shadow-lg transition-all duration-200 group cursor-pointer flex flex-col h-full border ${
        (request.status === 'declined' || request.status === 'cancelled')
          ? 'bg-gray-100 dark:bg-gray-900/80 border-gray-300 dark:border-gray-800 opacity-50 grayscale'
          : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'
      }`}
    >
      {/* Header: User & Status */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center space-x-3">
          <Avatar
            src={activeTab === 'received' ? request.sender?.profile_pic_signed : request.receiver?.profile_pic_signed}
            alt={activeTab === 'received' 
              ? `${request.sender?.first_name || 'Deleted'} ${request.sender?.last_name || 'User'}`
              : `${request.receiver?.first_name || 'Deleted'} ${request.receiver?.last_name || 'User'}`
            }
            size="md"
            className="ring-2 ring-gray-50 dark:ring-gray-700"
          />
          <div>
            <h3 className={`font-bold text-base ${
              (request.status === 'declined' || request.status === 'cancelled')
                ? 'text-gray-400 dark:text-gray-500 line-through'
                : 'text-gray-900 dark:text-white'
            }`}>
              {activeTab === 'received' 
                ? `${request.sender?.first_name || 'Deleted'} ${request.sender?.last_name || 'User'}`
                : `To: ${request.receiver?.first_name || 'Deleted'} ${request.receiver?.last_name || 'User'}`
              }
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <p className="text-xs text-teal-600 dark:text-teal-400 font-medium capitalize whitespace-nowrap">
                {request.type}
              </p>
              {request.service_package_id && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 whitespace-nowrap">
                  📦 Package Booking
                </span>
              )}
            </div>
          </div>
        </div>

        {(() => {
          if (request.status === 'accepted') {
            const isSender = user?.id === request.sender_id;
            const isReceiver = user?.id === request.receiver_id;
            const hasMarkedComplete = isSender ? request.sender_marked_complete : isReceiver ? request.receiver_marked_complete : false;
            const hasOtherMarkedComplete = isSender ? request.receiver_marked_complete : isReceiver ? request.sender_marked_complete : false;

            if (hasMarkedComplete && !hasOtherMarkedComplete) {
              return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide uppercase bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-800/50">
                  Awaiting Partner
                </span>
              );
            }
            if (!hasMarkedComplete && hasOtherMarkedComplete) {
              return (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide uppercase bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border border-teal-100 dark:border-teal-800/50 animate-pulse">
                  Partner Completed
                </span>
              );
            }
          }
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide uppercase ${
              request.status === 'pending'
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-100 dark:border-amber-800/50'
                : request.status === 'accepted'
                  ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50'
                  : request.status === 'completed'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800/50'
                    : request.status === 'declined'
                      ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-800/50'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
            }`}>
              {request.status}
            </span>
          );
        })()}
      </div>

      {/* Content */}
      <div className="mb-4 flex-grow">
        <h4 className={`font-semibold mb-1 text-sm line-clamp-1 ${
          (request.status === 'declined' || request.status === 'cancelled')
            ? 'text-gray-400 dark:text-gray-500'
            : 'text-gray-900 dark:text-white'
        }`}>{request.subject}</h4>
        <p className={`text-sm line-clamp-2 leading-relaxed ${
          (request.status === 'declined' || request.status === 'cancelled')
            ? 'text-gray-400 dark:text-gray-500'
            : 'text-gray-500 dark:text-gray-400'
        }`}>{request.message}</p>
      </div>

      {/* Footer: Metadata & Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-50 dark:border-gray-700 mt-auto">
        <div className="flex flex-col space-y-1 text-xs text-gray-400 dark:text-gray-500">
          <span className="flex items-center">
            <ClockIcon className="h-3.5 w-3.5 mr-1" />
            {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
          </span>
          
          {(request.budget !== null && request.budget !== undefined) && (
            <span className={`flex items-center font-medium ${
              (request.status === 'declined' || request.status === 'cancelled')
                ? 'text-gray-400 dark:text-gray-500'
                : 'text-gray-600 dark:text-gray-300'
            }`}>
              <CurrencyRupeeIcon className="h-3.5 w-3.5 mr-1" />
              {request.is_paid 
                ? formatCurrency(request.budget)
                : 'TFP'}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          {activeTab === 'received' && request.status === 'pending' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e?.stopPropagation(); handleResponse(request.id, 'declined'); }}
                className="!text-rose-600 dark:!text-rose-400 !border-rose-200 dark:!border-rose-800/50 hover:!bg-rose-50 dark:hover:!bg-rose-900/20 hover:!border-rose-300 dark:hover:!border-rose-700 !px-3 !py-1 text-xs"
              >
                Decline
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => { e?.stopPropagation(); handleResponse(request.id, 'accepted'); }}
                className="bg-orange-500 hover:bg-orange-600 !px-3 !py-1 text-xs"
              >
                Accept
              </Button>
            </>
          )}

          {request.status === 'accepted' && (() => {
            const isSender = user?.id === request.sender_id;
            const isReceiver = user?.id === request.receiver_id;
            const hasMarkedComplete = isSender ? request.sender_marked_complete : isReceiver ? request.receiver_marked_complete : false;
            const hasOtherMarkedComplete = isSender ? request.receiver_marked_complete : isReceiver ? request.sender_marked_complete : false;

            return (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e?.stopPropagation(); handleCancel(request); }}
                  className="!text-rose-600 dark:!text-rose-400 !border-rose-200 dark:!border-rose-800/50 hover:!bg-rose-50 dark:hover:!bg-rose-900/20 hover:!border-rose-300 dark:hover:!border-rose-700 !px-3 !py-1 text-xs"
                >
                  Cancel
                </Button>
                {hasMarkedComplete ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e?.stopPropagation(); handleRevokeComplete(request); }}
                    className="!text-amber-600 dark:!text-amber-400 !border-amber-200 dark:!border-amber-800/50 hover:!bg-amber-50 dark:hover:!bg-amber-900/20 !px-3 !py-1 text-xs font-medium"
                  >
                    Revoke Complete
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={(e) => { e?.stopPropagation(); handleComplete(request); }}
                    className="bg-orange-500 hover:bg-orange-600 !px-3 !py-1 text-xs"
                  >
                    {hasOtherMarkedComplete ? 'Confirm Complete' : 'Complete'}
                  </Button>
                )}
              </>
            );
          })()}

          {activeTab === 'sent' && request.status === 'pending' && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e?.stopPropagation(); handleWithdraw(request.id); }}
              className="text-gray-400 dark:text-gray-500 hover:text-rose-600 dark:hover:text-rose-400 border-gray-200 dark:border-gray-700 hover:border-rose-200 dark:hover:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/20 !px-3 !py-1 text-xs"
            >
              Withdraw
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 pt-1 pb-4 md:pt-1 md:pb-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {user?.user_type === 'hirer' ? 'Bookings' : 'Collaboration Requests'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {user?.user_type === 'hirer' 
              ? 'Manage your booking requests and responses' 
              : 'Manage your collaboration requests and responses'}
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            {/* Tab Navigation */}
            {user?.user_type !== 'hirer' && (
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-900 p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab('received')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'received'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  Received ({totalReceived})
                </button>
                <button
                  onClick={() => setActiveTab('sent')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'sent'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  Sent ({totalSent})
                </button>
              </div>
            )}

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none w-full sm:w-auto pl-4 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="completed">Completed</option>
                <option value="declined">Declined</option>
                <option value="cancelled">Cancelled</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Payment Filter */}
            <div className="relative">
              <select
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                className="appearance-none w-full sm:w-auto pl-4 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
              >
                <option value="all">All Payment</option>
                <option value="paid">Paid</option>
                <option value="tfp">TFP</option>
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>

            {/* Type Filter */}
            <div className="relative">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="appearance-none w-full sm:w-auto pl-4 pr-10 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
              >
                <option value="all">All Types</option>
                <option value="portrait">Portrait</option>
                <option value="fashion">Fashion</option>
                <option value="event">Event</option>
                <option value="commercial">Commercial</option>
                <option value="lifestyle">Lifestyle</option>
                <option value="tfp">TFP</option>
                <option value="project">Project</option>
                <option value="brand">Brand</option>
                <option value="other">Other</option>
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Requests Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 animate-pulse h-48">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : requests?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {requests.map(renderRequestCard)}
          </div>
        ) : (
          <div className="text-center py-12">
            <UserGroupIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No requests {activeTab}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {activeTab === 'received' 
                ? 'When others send you collaboration requests, they\'ll appear here'
                : 'Start collaborating by sending requests to other users'
              }
            </p>
            {activeTab === 'sent' && (
              <Button
                variant="primary"
                onClick={() => window.location.href = '/explore'}
              >
                Browse Users
              </Button>
            )}
          </div>
        )}

        {/* Infinite Scroll Sentinel & Loading Spinner */}
        {hasMore && !loading && (
          <div ref={sentinelRef} className="w-full flex justify-center py-8">
            {loadingMore && (
               <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 dark:border-gray-700 border-t-teal-600 dark:border-t-teal-400"></div>
            )}
          </div>
        )}
      </div>

      {/* Request Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onResponse={handleResponse}
          onWithdraw={handleWithdraw}
          onComplete={handleComplete}
          onRevokeComplete={handleRevokeComplete}
          onCancel={handleCancel}
          isReceived={activeTab === 'received'}
        />
      )}

      {/* Withdraw Confirmation Modal */}
      <ConfirmationModal
        isOpen={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        onConfirm={confirmWithdraw}
        title="Withdraw Request"
        message="Are you sure you want to withdraw this collaboration request? This action cannot be undone."
        confirmText="Withdraw"
        type="danger"
      />

      <ConfirmationModal
        isOpen={showCompleteConfirm}
        onClose={() => setShowCompleteConfirm(false)}
        onConfirm={confirmComplete}
        title="Confirm Completion"
        message="Mark this collaboration as complete? This action cannot be undone. Once both parties complete, the chat will remain open for 7 days before becoming read-only."
        confirmText="Yes, Complete"
        type="danger"
      />

      {/* Completion & Cancellation Lifecycle Modals */}
      <CollaborationCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onPostNow={onConfirmCompletionPostNow}
        onPostLater={onConfirmCompletionPostLater}
      />

      <CancellationReasonModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        onConfirm={onConfirmCancellation}
      />

      {showCreateCollabModal && requestToProcess && (
        <CreateCollaborationModal
          collaborationRequest={requestToProcess}
          onClose={() => setShowCreateCollabModal(false)}
          onSuccess={handlePostSuccess}
        />
      )}
    </div>
  );
};

export default CollaborationRequestsPage;
